import { ApiApi } from '@api';
import { NewFile } from '@types';
import { toast } from 'react-toastify';
import { ASSESSMENTS_IDS_KEY } from 'src/pages/questionnaire/data-collection';

import { AuthHelper } from './auth';
import { CookieHelper } from './cookie';
import { ErrorHelper } from './error';

/**
 * For date filtering.
 * Converts a Date to an ISO-8601 string using LOCAL time, avoiding UTC offset shift.
 * e.g. May 15 00:00 UTC+2 → "2026-05-15T00:00:00" instead of "2026-05-14T22:00:00Z"
 */
export const toLocalISOString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
};

/**
 * Scrolls the topmost field with a validation error into view. Every form field renders a DOM
 * element whose `id` matches its field name — on the `<input>` for `Input`, or on the wrapper `<div>`
 * for `CustomSelect`/`RichTextfield`/`CustomDateTimePicker` — so resolving by id works across field types.
 */
export const scrollToFirstErrorField = (fieldIds: string[]): void => {
  if (typeof document === 'undefined' || fieldIds.length === 0) return;

  const escape = (value: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value);
  const resolve = (name: string): HTMLElement | null => {
    const byId = document.getElementById(name);
    if (byId) return byId;
    const byName = document.getElementsByName(name)[0];
    if (byName instanceof HTMLElement) return byName;
    // fields that expose only a `name` (FileUpload) still render a <label htmlFor> via FormLabel
    return document.querySelector<HTMLElement>(`label[for="${escape(name)}"]`);
  };

  const elements = fieldIds.map(resolve).filter((el): el is HTMLElement => el instanceof HTMLElement);
  if (elements.length === 0) return;

  const target = elements.reduce((topmost, el) =>
    el.getBoundingClientRect().top < topmost.getBoundingClientRect().top ? el : topmost,
  );
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

/** Convenience wrapper for React Hook Form's `handleSubmit` onInvalid callback — pass the `errors` object directly. */
export const scrollToFirstError = (errors: Record<string, unknown>): void =>
  scrollToFirstErrorField(Object.keys(errors ?? {}));

interface ErrorObject {
  response: {
    message: string;
    status: number;
  };
}

interface FileAcceptType {
  [key: string]: string[];
}

const getApiBase = () => {
  const fromEnv = (process.env.NEXT_PUBLIC_API_BASE_URL || process.env.API_BASE_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') {
    return window.location.origin.replace(':3000', ':8000');
  }
  throw new Error('API base URL not configured (set NEXT_PUBLIC_API_BASE_URL or API_BASE_URL)');
};

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const runAsyncAssessments = (api: ApiApi) => {
  const runningAssessments = JSON.parse(CookieHelper.get(ASSESSMENTS_IDS_KEY) || '[]');
  if (runningAssessments?.length === 0) return;

  runningAssessments.forEach(async runningAssessment => {
    const interval = setInterval(async () => {
      if (runningAssessments.length === 0) clearInterval(interval);
      try {
        const hasAssassment = await api.apiAssessmentsRetrieve({
          id: runningAssessment,
        });

        if (hasAssassment.status !== 'RUNNING') {
          // remove assessment from list of async running assessments
          const assessments = JSON.parse(CookieHelper.get(ASSESSMENTS_IDS_KEY) || '[]');

          CookieHelper.set(
            ASSESSMENTS_IDS_KEY,
            JSON.stringify(assessments.filter(assessment => assessment !== runningAssessment)),
          );

          if (hasAssassment.status === 'SUCCESS') {
            toast.success('Assessment is successfully finished!');
          }
          if (hasAssassment.status === 'SUCCESS_WITH_ERRORS') {
            toast.success('Assessment is successfully finished with errors!');
          }
          if (hasAssassment.status === 'FAILED') {
            toast.error('Assessment has failed. Please check your answers and submit questionnaire form again.');
          }

          clearInterval(interval);
        }
      } catch (error: any) {
        if ((error as ErrorObject).response?.status === 404) {
          // questionnaire object not exists => remove id from cookie
          const assessments = JSON.parse(CookieHelper.get(ASSESSMENTS_IDS_KEY) || '[]');

          CookieHelper.set(
            ASSESSMENTS_IDS_KEY,
            JSON.stringify(assessments.filter(assesment => assesment !== runningAssessment)),
          );
        }
      }
    }, 10000);
  });
};

const saveFile = (data: any, name: any, type?: string) => {
  const a = document.createElement('a');
  const blob = new Blob([data], { type: type || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  document.body.appendChild(a);
  a.style.display = 'none';
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const downloadFile = async (url: string | NewFile | File, name: string, acceptTypes?: FileAcceptType) => {
  let raw: any;

  // Local browser objects / URLs should never go through /api/download
  if (url instanceof File) {
    saveFile(url, name, Object.keys({ ...acceptTypes })[0]);
    return;
  }

  if (typeof url !== 'string') {
    raw = (url as NewFile).file;
    saveFile(raw, name, Object.keys({ ...acceptTypes })[0]);
    return;
  }

  if (url.startsWith('blob:') || url.startsWith('data:')) {
    const response = await fetch(url);
    raw = await response.blob();
    saveFile(raw, name, Object.keys({ ...acceptTypes })[0] || 'application/octet-stream');
    return;
  }

  const response = await fetch(
    `${
      process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin
    }/api/download/?path=${encodeURIComponent(url)}`,
    {
      credentials: 'include',
      headers: { 'X-CSRFTOKEN': CookieHelper.get('csrf'), Authorization: `Bearer ${CookieHelper.get('access_token')}` },
    },
  );
  if (response.status >= 200 && response.status < 209) {
    raw = await response.blob();
  } else if (response.status === 401) {
    AuthHelper.checkAndRefreshAccessToken(async () => {
      const retryResponse = await fetch(`${window.location.origin}/api/download/?path=${encodeURIComponent(url)}`, {
        credentials: 'include',
        headers: {
          'X-CSRFTOKEN': CookieHelper.get('csrf'),
          Authorization: `Bearer ${CookieHelper.get('access_token')}`,
        },
      });
      if (retryResponse.status >= 200 && retryResponse.status < 209) {
        raw = await retryResponse.blob();
      } else {
        const responseMessage = await retryResponse.json();
        ErrorHelper.handleErrorNotification(responseMessage);
      }
    });
  }
  saveFile(raw, name, Object.keys({ ...acceptTypes })[0] || 'application/octet-stream');
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text || typeof navigator === 'undefined' || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// Carries the HTTP status so the file preview can distinguish auth-expiry (401),
// forbidden (403), missing (404) and network failure (null) — each has a distinct message.
export class FileFetchError extends Error {
  status: number | null;

  constructor(status: number | null) {
    super(status === null ? 'network' : `status ${status}`);
    this.name = 'FileFetchError';
    this.status = status;
  }
}

// Fetches an uploaded file's bytes through the existing download endpoint, returning
// the Blob instead of triggering a save. Same auth as downloadFile.
const fetchFileBlob = async (path: string): Promise<Blob> => {
  const base = process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin;

  let response: Response;
  try {
    response = await fetch(`${base}/api/download/?path=${encodeURIComponent(path)}`, {
      credentials: 'include',
      headers: { 'X-CSRFTOKEN': CookieHelper.get('csrf'), Authorization: `Bearer ${CookieHelper.get('access_token')}` },
    });
  } catch {
    throw new FileFetchError(null);
  }

  if (response.status >= 200 && response.status < 209) {
    return response.blob();
  }
  throw new FileFetchError(response.status);
};

export const GeneralHelper = {
  getApiBase,
  runAsyncAssessments,
  scrollToTop,
  downloadFile,
  fetchFileBlob,
  saveFile,
  copyToClipboard,
};
